import { Box, Typography } from "@mui/material";
import { FC } from "react";

export type ProgressPieBadgeProps = {
  /** Percentage 0-100. Nothing is rendered when omitted. */
  value?: number;
  size?: number;
  color?: string;
  trackColor?: string;
  outlineColor?: string;
  outlineWidth?: number;
};

/** A small pie chart (filled proportionally to `value`) with the percentage to its right. */
export const ProgressPieBadge: FC<ProgressPieBadgeProps> = ({
  value,
  size = 20,
  color = "#3B82F6",
  trackColor = "transparent",
  outlineWidth = 2,
  outlineColor,
}) => {
  if (value == null) return null;

  outlineColor ??= color;

  const clamped = Math.min(100, Math.max(0, value));
  const center = size / 2;
  const radius = center - outlineWidth / 2;
  const angle = (clamped / 100) * 360;
  const angleRad = ((angle - 90) * Math.PI) / 180;
  const x = center + radius * Math.cos(angleRad);
  const y = center + radius * Math.sin(angleRad);
  const largeArcFlag = angle > 180 ? 1 : 0;
  const isFull = clamped >= 100;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill={trackColor}
          stroke={outlineColor}
          strokeWidth={outlineWidth}
        />
        {clamped > 0 &&
          (isFull ? (
            <circle cx={center} cy={center} r={radius} fill={color} />
          ) : (
            <path
              d={`M ${center} ${center} L ${center} ${center - radius} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x} ${y} Z`}
              fill={color}
            />
          ))}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={outlineColor}
          strokeWidth={outlineWidth}
        />
      </svg>
      <Typography
        variant="body1"
        sx={{ fontWeight: 500, color, fontSize: size * 0.625 }}
      >
        {Math.round(clamped)}%
      </Typography>
    </Box>
  );
};
